import { useCallback, useEffect, useRef, useState } from "react";

import { GET_EMPLOYEE_PROFILE_FIELDS } from "../../../../../config/Endpoints.js";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type {
  EmployeeDirectoryCardData,
  EmployeeDirectoryFilters,
  EmployeeProfileFields,
} from "../types.ts";

const PAGE_SIZE = 10;
const pendingDirectoryRequests = new Map<string, Promise<unknown>>();

function getDirectoryPage(url: string): Promise<unknown> {
  const pending = pendingDirectoryRequests.get(url);
  if (pending) return pending;

  const request = getApi<unknown>(url, accessToken())
    .then(({ data }) => data)
    .finally(() => pendingDirectoryRequests.delete(url));
  pendingDirectoryRequests.set(url, request);
  return request;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown, fallback = "-"): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function parseProfileFields(row: Record<string, unknown>): EmployeeProfileFields {
  const basic = record(row.basicDetails);
  const insurance = record(row.insuranceDetails ?? basic.insuranceDetails);
  const work = record(row.workDetails);
  const personal = record(row.personalDetails);
  return {
    employeeId: text(row.employeeId),
    basicDetails: {
      projectEligibility: typeof basic.projectEligibility === "boolean" ? basic.projectEligibility : null,
      emailAddress: text(basic.emailAddress), primaryEmail: text(basic.primaryEmail), middleName: text(basic.middleName),
      lastName: text(basic.lastName), firstName: text(basic.firstName),
      insuranceCardNumber: text(
        basic.insuranceCardNumber ?? basic.insuranceNumber ?? insurance.insuranceNumber,
      ),
      insuranceValidFrom: text(
        basic.insuranceFrom ?? insurance.insuranceFrom ?? row.insuranceFrom,
      ),
      insuranceValidTill: text(
        basic.insuranceTo ?? insurance.insuranceTo ?? row.insuranceTo,
      ),
    },
    workDetails: {
      department: text(work.department), dateOfJoining: text(work.dateOfJoining),
      techStack: text(work.techStack), designation: text(work.designation),
      employeeType: text(work.employeeType), reportingManager: text(work.reportingManager),
      employeeSubType: text(work.employeeSubType), leaveManager: text(work.leaveManager),
      currentLocation: text(work.currentLocation), joiningLocation: text(work.joiningLocation),
      location: text(work.location),
      trailhead: text(work.trailhead),
    },
    personalDetails: {
      bloodGroup: text(personal.bloodGroup), dateOfBirth: text(personal.dateOfBirth),
    },
  };
}

function parseEmployee(value: unknown): EmployeeDirectoryCardData | null {
  const row = record(value);
  const basicDetails = record(row.basicDetails);
  const workDetails = record(row.workDetails);
  const rawId = row.id ?? row.userId ?? row.employeeDbId ?? basicDetails.id ?? row.employeeId;
  if (rawId == null || String(rawId).trim() === "") return null;
  const fullName = [
    basicDetails.firstName,
    basicDetails.middleName,
    basicDetails.lastName,
  ].filter((part) => typeof part === "string" && part.trim()).join(" ");
  return {
    id: typeof rawId === "number" ? rawId : String(rawId),
    employeeCode: text(row.employeeId ?? row.empId),
    employeeName: text(row.empName ?? row.employeeName ?? fullName),
    designation: text(row.designation ?? workDetails.designation),
    department: text(row.department ?? workDetails.department),
    employeeType: text(row.employeeType ?? workDetails.employeeType),
    dateOfJoining: text(row.dateOfJoining ?? row.joiningDate ?? workDetails.dateOfJoining),
    photoPath: typeof (row.photoPath ?? basicDetails.photoPath) === "string"
      ? String(row.photoPath ?? basicDetails.photoPath).trim()
      : undefined,
    profile: parseProfileFields(row),
  };
}

function parsePage(payload: unknown) {
  const outer = record(payload);
  const body = outer.data && !Array.isArray(outer.data) ? record(outer.data) : outer;
  const content = Array.isArray(body.content) ? body.content : [];
  const rows = content.flatMap((item) => {
    const employee = parseEmployee({
      ...record(item),
      insuranceNumber: record(item).insuranceNumber ?? body.insuranceNumber,
      insuranceFrom: record(item).insuranceFrom ?? body.insuranceFrom,
      insuranceTo: record(item).insuranceTo ?? body.insuranceTo,
    });
    return employee ? [employee] : [];
  });
  const totalPages = Number(body.totalPages);
  return {
    rows,
    totalElements: Number(body.totalElements) || rows.length,
    totalPages: Number.isFinite(totalPages) ? totalPages : 1,
  };
}

export function useEmployeeViewDirectory(filters: EmployeeDirectoryFilters) {
  const [employees, setEmployees] = useState<EmployeeDirectoryCardData[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const fetchPage = useCallback(async (pageNumber: number, reset: boolean) => {
    const id = ++requestId.current;
    reset ? setLoading(true) : setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(pageNumber),
        size: String(PAGE_SIZE),
      });
      if (filters.employeeId != null) params.set("employeeId", String(filters.employeeId));

      const data = await getDirectoryPage(
        `${GET_EMPLOYEE_PROFILE_FIELDS}?${params.toString()}`,
      );
      if (id !== requestId.current) return;
      const parsed = parsePage(data);
      setEmployees((current) => reset ? parsed.rows : [...current, ...parsed.rows]);
      setTotalElements(parsed.totalElements);
      setPage(pageNumber);
      setHasMore(pageNumber + 1 < parsed.totalPages && parsed.rows.length > 0);
    } catch (cause) {
      if (id !== requestId.current) return;
      if (reset) setEmployees([]);
      setError(cause instanceof Error ? cause.message : "Failed to load employees.");
      setHasMore(false);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    setEmployees([]);
    setPage(0);
    setHasMore(true);
    void fetchPage(0, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) void fetchPage(page + 1, false);
  }, [fetchPage, hasMore, loading, loadingMore, page]);

  return { employees, totalElements, loading, loadingMore, hasMore, error, loadMore };
}
